import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, KeyRound, User, Mail, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AccountsState } from '../types';
import { findAccountByUsername, findAccountByLinkCode, findAccountByEmail, verifyPassword } from '../logic/auth';
import { DjayAvatar, DjayBubble } from '../../mascot';
/**
 * LoginScreen — Warm, mascot-guided login experience.
 *
 * Two modes: Learner (username or link code) and Adult (email + password).
 * Djay guides the user with contextual messages.
 * Smooth tab transitions with animated indicator.
 */
interface LoginScreenProps {
  onLoginSuccess: (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => void;
  onBack: () => void;
  onNavigateToRegister?: () => void;
  accounts: AccountsState;
}
type LoginTab = 'learner' | 'adult';
const TAB_MESSAGES: Record<LoginTab, {
  text: string;
  emotion: 'happy' | 'encouraging';
}> = {
  learner: {
    text: 'Welkom terug! Vul je gegevens in 😊',
    emotion: 'happy'
  },
  adult: {
    text: 'Hallo! Log in met je e-mailadres.',
    emotion: 'encouraging'
  }
};
export function LoginScreen({
  onLoginSuccess,
  onBack,
  onNavigateToRegister,
  accounts
}: LoginScreenProps) {
  const [activeTab, setActiveTab] = useState<LoginTab>('learner');
  const [loginMethod, setLoginMethod] = useState<'username' | 'linkcode'>('username');
  const [username, setUsername] = useState('');
  const [linkCode, setLinkCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const djayMsg = error ? {
    text: 'Hmm, dat klopt niet helemaal. Probeer het nog eens!',
    emotion: 'thinking' as const
  } : TAB_MESSAGES[activeTab];
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (isLoading) return;
    setIsLoading(true);
    try {
      if (activeTab === 'learner') {
        if (loginMethod === 'username') {
          const account = findAccountByUsername(accounts, username);
          if (account) {
            if (account.passwordHash) {
              const isValid = await verifyPassword(password, account.passwordHash);
              if (!isValid) {
                setError('Wachtwoord is onjuist.');
                setShakeKey(k => k + 1);
                return;
              }
            }
            onLoginSuccess(account.id, account.role);
          } else {
            setError('Gebruikersnaam niet gevonden.');
            setShakeKey(k => k + 1);
          }
        } else {
          const account = findAccountByLinkCode(accounts, linkCode);
          if (account) {
            onLoginSuccess(account.id, account.role);
          } else {
            setError('Koppelcode is ongeldig of niet gevonden.');
            setShakeKey(k => k + 1);
          }
        }
      } else {
        const account = findAccountByEmail(accounts, email);
        if (account) {
          if (account.passwordHash) {
            const isValid = await verifyPassword(password, account.passwordHash);
            if (!isValid) {
              setError('Wachtwoord is onjuist.');
              setShakeKey(k => k + 1);
              return;
            }
          }
          onLoginSuccess(account.id, account.role);
        } else {
          setError('E-mailadres niet gevonden.');
          setShakeKey(k => k + 1);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const switchTab = (tab: LoginTab) => {
    setActiveTab(tab);
    setError('');
    setPassword('');
  };
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/30 to-white flex items-center justify-center p-4 sm:p-8 relative overflow-hidden" data-id="element-159">
      <div className="w-full max-w-md relative z-10" data-id="element-160">
        {/* Back button */}
        <motion.button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-6 transition-colors font-semibold text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-161">
          <ArrowLeft className="w-4 h-4" data-id="element-162" /> Terug
        </motion.button>

        {/* Djay guide */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-163">
          <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-164">
            <DjayAvatar size="lg" emotion={djayMsg.emotion} animate={true} data-id="element-165" />
          </motion.div>
          <div className="mt-3 w-full max-w-[240px]" data-id="element-166">
            <AnimatePresence mode="wait" data-id="element-167">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} transition={{
              duration: 0.2
            }} data-id="element-168">
                <DjayBubble message={djayMsg.text} position="top" autoDismissMs={0} colorScheme="amber" data-id="element-169" />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div key={shakeKey} className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden" initial={{
        opacity: 0,
        y: 20
      }} animate={shakeKey > 0 ? {
        x: [-10, 10, -10, 10, 0],
        transition: {
          duration: 0.4
        }
      } : {
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: shakeKey > 0 ? 0 : 0.1
      }} data-id="element-170">
          <div className="p-6 sm:p-8" data-id="element-171">
            {/* Title */}
            <h1 className="text-2xl font-black text-slate-900 text-center mb-6" data-id="element-172">
              Inloggen
            </h1>

            {/* Tab switcher with animated indicator */}
            <div className="relative flex p-1 bg-slate-100 rounded-2xl mb-6" data-id="element-173">
              <motion.div className="absolute top-1 bottom-1 rounded-xl bg-white shadow-sm" initial={false} animate={{
              left: activeTab === 'learner' ? '4px' : '50%',
              width: 'calc(50% - 4px)'
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30
            }} data-id="element-174" />
              <button type="button" onClick={() => switchTab('learner')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'learner' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-175">
                🎒 Leerling
              </button>
              <button type="button" onClick={() => switchTab('adult')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'adult' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-176">
                👤 Ouder / Docent
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-4" data-id="element-177">
              <AnimatePresence mode="wait" data-id="element-178">
                {activeTab === 'learner' && <motion.div key="learner-form" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} transition={{
                duration: 0.25
              }} className="space-y-4" data-id="element-179">
                    {/* Login method toggle */}
                    <div className="flex p-1 bg-slate-50 rounded-xl border border-slate-200" data-id="element-180">
                      <button type="button" onClick={() => setLoginMethod('username')} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all ${loginMethod === 'username' ? 'bg-orange-50 text-orange-700 border border-orange-200 shadow-sm' : 'text-slate-500 border border-transparent'}`} data-id="element-181">
                        Gebruikersnaam
                      </button>
                      <button type="button" onClick={() => setLoginMethod('linkcode')} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all ${loginMethod === 'linkcode' ? 'bg-orange-50 text-orange-700 border border-orange-200 shadow-sm' : 'text-slate-500 border border-transparent'}`} data-id="element-182">
                        Koppelcode
                      </button>
                    </div>

                    <AnimatePresence mode="wait" data-id="element-183">
                      {loginMethod === 'username' ? <motion.div key="username-fields" initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} transition={{
                    duration: 0.2
                  }} className="space-y-4" data-id="element-184">
                          <div data-id="element-185">
                            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-186">
                              Gebruikersnaam
                            </label>
                            <div className="relative" data-id="element-187">
                              <Input type="text" required value={username} onChange={e => setUsername(e.target.value)} icon={<User className="w-5 h-5" data-id="element-189" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="bijv. djayden" autoComplete="username" data-id="element-188" />
                            </div>
                          </div>
                          <div data-id="element-190">
                            <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-191">
                              Wachtwoord
                            </label>
                            <div className="relative" data-id="element-192">
                              <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-194" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-193" />
                            </div>
                          </div>
                        </motion.div> : <motion.div key="linkcode-field" initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-195">
                          <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-196">
                            Koppelcode (van je docent)
                          </label>
                          <div className="relative" data-id="element-197">
                            <Input type="text" required value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} icon={<KeyRound className="w-5 h-5" data-id="element-199" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 font-mono text-xl tracking-[0.3em] uppercase text-center py-3.5" placeholder="ABCDEF" maxLength={6} autoComplete="off" data-id="element-198" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>}

                {activeTab === 'adult' && <motion.div key="adult-form" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.25
              }} className="space-y-4" data-id="element-200">
                    <div data-id="element-201">
                      <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-202">
                        E-mailadres
                      </label>
                      <div className="relative" data-id="element-203">
                        <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-205" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="naam@voorbeeld.nl" autoComplete="email" data-id="element-204" />
                      </div>
                    </div>
                    <div data-id="element-206">
                      <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-207">
                        Wachtwoord
                      </label>
                      <div className="relative" data-id="element-208">
                        <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-210" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-209" />
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Error message */}
              <AnimatePresence data-id="element-211">
                {error && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-212">
                    <div className="p-3 bg-red-50 text-red-700 text-sm font-medium rounded-2xl border border-red-200 flex items-start gap-2" data-id="element-213">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-214" />
                      <span data-id="element-215">{error}</span>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading} data-id="element-216">
                {isLoading ? 'Even geduld...' : 'Inloggen'}
              </Button>
            </form>

            {/* Cross-link to Register */}
            {onNavigateToRegister && <div className="mt-6 pt-5 border-t border-slate-100 text-center" data-id="element-217">
                <p className="text-slate-500 font-medium text-sm" data-id="element-218">
                  Nog geen account?{' '}
                  <button onClick={onNavigateToRegister} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-219">
                    Maak er een aan
                  </button>
                </p>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}